import {SchemaPlayer} from "./player/SchemaPlayer";
import {WebSocketClient} from "./WebSocketClient";
import {createAnchorAndClick, hasHidden, toggleHidden} from "./lib/domFunctions";
import {apiGetForAjaxResponse, apiPostForAjaxResponse} from "./lib/SecuredAjax";
import {PositionPopover} from "./dashboard/PositionPopover";
import {TransportPopover} from "./dashboard/TransportPopover";
import {fetchJsonForApiResponse, toastFetchError} from "./lib/fetch";
import {SchemaState} from "./SchemaState";
import {ProcessControlService_Mode, ProcessControlService_Status} from "./dto/com.rico.sb2.service";
import {AppConfig} from "./AppConfig";
import {PlayerStateData} from "./dto/com.rico.sb2.controllers.dto";
import {buttonProgress} from "./lib/buttonProgress";
import {PlayerDataAdapter} from "./PlayerDataAdapter";
import {ContainerPopover} from "./dashboard/ContainerPopover";
import {DashboardForm} from "./DashboardForm";
import {ContainerTypeInfo} from "./dto/com.rico.sb2.service.queue";
import {EmulatorTimeInfo, getEmulatorTime, setEmulatorTime} from "./dto/com.rico.sb2.service.timeprovider";
import {AppBus, AppEvents} from "./AppBus";
import {SinglePopover} from "./lib/bootstrapPopover";
import {bootstrapPrompt} from "./lib/bootstrapModal";
import {Messages} from "./messages/Messages";
import {AuthorityProvider} from "./lib/ContainerActionRules";
import {showToastError, showToastSuccess} from "./lib/boostrapToast";
import {DashboardWidgetContext} from "./dashboard/widgets/DashboardWidget";
import {DashboardLayoutStore} from "./dashboard/widgets/DashboardLayoutStore";
import {createDashboardWidgetRegistry} from "./dashboard/widgets/dashboardWidgetDescriptors";
import {WidgetHost} from "./dashboard/widgets/WidgetHost";
import {WidgetsSettingsModal} from "./dashboard/widgets/WidgetsSettingsModal";

const messages = new Messages();

interface DashboardParams {
	mode?: ProcessControlService_Mode,
	modeSwitches?: any,
	schemaState: SchemaState,
	allowedDensities: number[],
	containerTypes: ContainerTypeInfo[],
	canControl: boolean,
	emulatorTimeInfo?: EmulatorTimeInfo
}

export class Dashboard {
	private readonly ws: WebSocketClient
	private readonly schemaState: SchemaState
	private readonly playerData: PlayerDataAdapter
	private readonly allowedDensities: number[]

	private readonly display: HTMLElement
	private readonly canControl: boolean

	private player: SchemaPlayer | null = null;

	private readonly modeSwitches: any
	private readonly modeSwitchesButtons: Map<ProcessControlService_Mode, HTMLButtonElement> = new Map()
	private readonly modeSwitchesAvailable = new Set<ProcessControlService_Mode>();

	private readonly widgetHost: WidgetHost

	private dashboardForm: DashboardForm | null = null

	private emulatorTimeValueText: HTMLElement | null = null
	private emulatorTimeFactorOn: HTMLElement | null = null

	constructor({mode, modeSwitches, canControl, schemaState, allowedDensities, emulatorTimeInfo}: DashboardParams) {
		this.display = document.getElementById('schemaDisplay')!!
		this.canControl = canControl
		this.allowedDensities = allowedDensities

		this.schemaState = schemaState
		this.schemaState.modeListeners.add({
			onServiceModeChanged: () => this.updateStatusUI()
		})

		console.info(`use programs with intermediate points`)
		SchemaPlayer.withIntermediatePoints();

		this.playerData = new PlayerDataAdapter(this.schemaState)

		this.emulatorTimeValueText = document.querySelectorAll(`[data-bind="emulatorTimeValueText"]`)[0] as HTMLElement || null
		this.emulatorTimeFactorOn = document.querySelectorAll(`[data-bind="emulatorTimeFactorOn"]`)[0] as HTMLElement || null
		setEmulatorTime(emulatorTimeInfo || null);
		this.displayEmulatorTime()
		if (emulatorTimeInfo) {
			setInterval(this.displayEmulatorTime.bind(this), 500);
		}

		const widgetContext: DashboardWidgetContext = {schemaState: this.schemaState, playerData: this.playerData}
		this.widgetHost = new WidgetHost(
			document.getElementById('dashboardWidgetsHost')!,
			createDashboardWidgetRegistry(),
			new DashboardLayoutStore(AppConfig.userLogin),
			widgetContext
		)

		const authorityProvider = new AuthorityProvider();
		if (authorityProvider.hasAuthority('switch_mode:auto')) {
			this.modeSwitchesAvailable.add(ProcessControlService_Mode.AUTOMATIC)
			this.modeSwitchesAvailable.add(ProcessControlService_Mode.SEMIAUTOMATIC)
			this.modeSwitchesAvailable.add(ProcessControlService_Mode.PAUSED)
			this.modeSwitchesAvailable.add(ProcessControlService_Mode.STOPPED)
		}
		if (authorityProvider.hasAuthority('switch_mode:any')) {
			Object.values(ProcessControlService_Mode).forEach(v => this.modeSwitchesAvailable.add(v))
		}

		this.modeSwitches = modeSwitches || {}
		document.querySelectorAll<HTMLButtonElement>('button[data-bind="switchMode"][data-arg]').forEach(button => {
			const mode = button.getAttribute('data-arg') as ProcessControlService_Mode
			this.modeSwitchesButtons.set(mode, button)
			button.addEventListener('click', () => this.switchModeAsync(mode as ProcessControlService_Mode))
		})
		this.updateStatusUI();

		this.ws = WebSocketClient.instance();
		this.playerData.subscribeToUpdates(this.ws)
		this.schemaState.subscribeToUpdates(this.ws)
		this.ws.listeners.add({
			onConnected: this.onWebSocketConnected.bind(this)
		})
		this.ws.subscribe('/topic/time', (m?: EmulatorTimeInfo) => {
			setEmulatorTime(m || null);
			this.displayEmulatorTime()
		});

		if (this.ws.connected) {
			this.onWebSocketConnected();
		}

		const showCreateContainerFormHidden = AppConfig.roles.length == 0;
		document.querySelectorAll<HTMLButtonElement>('button[data-bind="showCreateContainerForm"]').forEach(node => toggleHidden(node, showCreateContainerFormHidden));
		document.querySelectorAll<HTMLElement>('[data-bind="hideContainerForm"]')
			.forEach(node => node.addEventListener('click', () => this.displayContainerForm(null)));
		window.addEventListener('resize', () => this.updateWidgetsGrid());

		AppBus.addEventListener(AppEvents.VIEW_CONTAINER, (id: number, inplace?: boolean) => {
			SinglePopover.hideCurrent()
			this.viewContainer(id, inplace)
		})
		AppBus.addEventListener(AppEvents.EDIT_CONTAINER, (id: number) => {
			SinglePopover.hideCurrent()
			createAnchorAndClick(`${AppConfig.CP}/queue/${id}/edit`, true)
		})
		AppBus.addEventListener(AppEvents.PRINT_CONTAINER, (id: number) => {
			SinglePopover.hideCurrent()
			const printWindow = window.open(`${AppConfig.CP}/queue/${id}`, '_blank')!;
			printWindow.addEventListener('load', function () {
				if (Boolean((printWindow as any).chrome)) {
					printWindow.print();
					setTimeout(function () {
						printWindow.close();
					}, 250);
				} else {
					printWindow.print();
					printWindow.close();
				}
			}, true);
		})
	}

	private onWebSocketConnected() {
		console.info('refresh status')
		apiGetForAjaxResponse(`${AppConfig.CP}/op/status`).then((m: ProcessControlService_Status) => this.schemaState.mode = m.mode)

		console.info('refresh player');
		apiGetForAjaxResponse(`${AppConfig.CP}/op/playerStateData`).then((m: PlayerStateData) => {
			this.displaySvg(m)
			this.widgetHost.resetWidgets()
		})
	}

	private updateStatusUI() {
		const nextStates = this.schemaState.mode == null ? [] : this.modeSwitches[this.schemaState.mode] || []
		const canTo = (state: ProcessControlService_Mode) => nextStates.indexOf(state) >= 0 && this.modeSwitchesAvailable.has(state);
		for (const [mode, button] of this.modeSwitchesButtons) {
			toggleHidden(button, !this.canControl || !canTo(mode));
		}
	}

	private async switchModeAsync(mode: ProcessControlService_Mode) {
		const confirmed = mode != ProcessControlService_Mode.STOPPED || await bootstrapPrompt({
			title: messages.get('dashboard.action.switchToEmergency'),
			body: `<div class="text-center">${messages.get('dashboard.action.switchToEmergency.confirm')}</div>`,
			buttonOk: messages.get('boolean.yes'),
			buttonOkClass: 'btn-danger'
		})
		if (!confirmed)
			return;

		const progress = buttonProgress(this.modeSwitchesButtons.get(mode));
		apiPostForAjaxResponse(`/switchMode?mode=${mode}`)
			.catch(reason => toastFetchError(reason))
			.then(() => progress.stop());
	}

	private displaySvg(init: PlayerStateData) {
		this.player?.clear()
		this.player = null

		const playerData = init.playerData
		this.playerData.reset(playerData, init.initialMessages || [])

		if (!init.playerSvg) return;

		this.player = new SchemaPlayer(this.display, init.playerSvg, init.mirrorSchema, playerData.containerSvg, playerData.containerCss, this.playerData, init.initialMessages || [], {
			getContainerPopover: (player: SchemaPlayer, id: number) => new ContainerPopover(this.playerData, id),
			getPositionPopover: (player: SchemaPlayer, id: number) => new PositionPopover(this.playerData, id),
			getTransportPopover: (player: SchemaPlayer, id: number) => new TransportPopover(this.playerData, id),
		});

		this.showHideDashboardWidgets();
	}

	private showHideDashboardWidgets() {
		const dashboardWidgets = document.getElementById('dashboardWidgets');
		toggleHidden(dashboardWidgets, false);

		this.updateWidgetsGrid();
	}

	private updateWidgetsGrid() {
		const dashboardWidgets = document.getElementById('dashboardWidgets');
		if (!dashboardWidgets || hasHidden(dashboardWidgets)) return;

		const host = this.widgetHost.element;
		const fontSize = parseFloat(getComputedStyle(host).fontSize) || 12;
		const rows: 1 | 2 = dashboardWidgets.clientHeight >= 640 ? 2 : 1;
		const cellHeight = Math.max(0, (dashboardWidgets.clientHeight - fontSize - (rows - 1) * fontSize) / rows);

		host.style.setProperty('--widget-rows', `${rows}`);
		host.style.setProperty('--widget-cell-h', `${cellHeight}px`);
		this.widgetHost.setRowsAvailable(rows);
	}

	showWidgetsSettings() {
		WidgetsSettingsModal.show(this.widgetHost)
	}

	showCreateContainerForm(always: boolean = false) {
		this.displayContainerForm(this.buildContainerForm());
	}

	private buildContainerForm() {
		return new DashboardForm({allowedDensities: this.allowedDensities});
	}

	private displayContainerForm(dashboardForm: DashboardForm | null) {
		const containerForm = document.getElementById('containerForm');

		if (this.dashboardForm) {
			this.dashboardForm.element.remove()
			this.dashboardForm = null
		}

		this.dashboardForm = dashboardForm;
		toggleHidden(containerForm, dashboardForm == null);

		if (this.dashboardForm) {
			this.dashboardForm.postSubmit = () => {
				this.displayContainerForm(null);
			};
			this.dashboardForm.postCancel = () => {
				this.displayContainerForm(null);
			};
			containerForm?.append(this.dashboardForm.element)
		}
	}

	private viewContainer(id: number, inplace: boolean = false) {
		if (!inplace) {
			createAnchorAndClick(`${AppConfig.CP}/queue/${id}`, true)
			return;
		}

		this.displayContainerForm(null);

		fetchJsonForApiResponse(`${AppConfig.CP}/queue/${id}/edit`)
			.then(form => {
				if (!form.editable) {
					createAnchorAndClick(`${AppConfig.CP}/queue/${id}`, true)
					return
				}
				this
					.buildContainerForm()
					.initWithForm(id, form)
					.then(form => this.displayContainerForm(form));
			})
			.catch(toastFetchError)
	}

	setTimeFactor(n: number) {
		if (!Number.isFinite(n)) return

		apiPostForAjaxResponse(`/setTimeFactor?timeFactor=${JSON.stringify(n)}`)
			.then(success => {
				if (success === true) showToastSuccess('Коэффициент времени изменен')
				else showToastError('Коэффициент времени не изменился')
			})
			.catch(reason => toastFetchError(reason))
	}

	private displayEmulatorTime() {
		const emulatorTime = getEmulatorTime()
		if (!emulatorTime) return

		if (this.emulatorTimeValueText) {
			const text = emulatorTime.nowDate().toLocaleString();
			if (this.emulatorTimeValueText.innerText !== text)
				this.emulatorTimeValueText.innerText = text;
		}
		if (this.emulatorTimeFactorOn) {
			const timeFactor = emulatorTime.info?.timeFactor;
			const timeVelocityNotOne = emulatorTime.info?.timeFactorUsed && timeFactor != 1.0;
			this.emulatorTimeFactorOn.classList.toggle('hidden', !timeVelocityNotOne)
			this.emulatorTimeFactorOn.innerHTML = `${timeFactor} <i class="bi bi-clock-history"></i>`;
			this.emulatorTimeFactorOn.parentElement?.setAttribute("title", timeVelocityNotOne ? "Время течет с другой скоростью" : "")
		}
	}
}
